import { ComponentProps } from "react";
import { useSnackbar } from "notistack";
import { useModal, UpdateModal } from "../../../components";
import { TimeInLieu } from "../../../API";
import { useTimeInLieuLib } from "../../../lib";
import { RequestModal } from "./RequestModal";

export const useRequestModals = () => {
  const {
    createTimeInLieuRecord,
    updateTimeInLieuRecord,
    deleteTimeInLieuRecord,
  } = useTimeInLieuLib();
  const { enqueueSnackbar } = useSnackbar();
  const showSnackbar = (message: string, error = false) => {
    enqueueSnackbar(message, { variant: error ? "error" : "success" });
  };

  const postAction = (
    message: string,
    isError: boolean,
    callback: () => void
  ) => {
    showSnackbar(message, isError);
    callback();
  };

  const { showModal: showCreateModal, hideModal: hideCreateModal } = useModal<
    ComponentProps<typeof RequestModal>,
    {
      timeInLieus: TimeInLieu[];
      employeeID: string;
    }
  >({
    component: RequestModal,
    data: {
      title: "Request Time In Lieu",
      onSubmit: ({ data }) =>
        createTimeInLieuRecord(data, {
          onSuccess: () =>
            postAction(
              "Your time in lieu request has been submitted.",
              false,
              hideCreateModal
            ),
          onError: () =>
            postAction(
              "Error requesting time in lieu. Please try again in a few seconds",
              true,
              hideCreateModal
            ),
        }),
      testID: "create-time-in-lieu-request",
    },
  });

  const { showModal: showUpdateModal, hideModal: hideUpdateModal } = useModal<
    ComponentProps<typeof RequestModal>,
    {
      row: TimeInLieu;
      timeInLieus: TimeInLieu[];
      employeeID: string;
    }
  >({
    component: RequestModal,
    data: {
      title: "Update Time In Lieu",
      onSubmit: ({ data }) =>
        !data.id
          ? null
          : updateTimeInLieuRecord(
              {
                ...data,
                id: data.id,
              },
              {
                onSuccess: () =>
                  postAction(
                    "Your time in lieu request has been submitted.",
                    false,
                    hideUpdateModal
                  ),
                onError: () =>
                  postAction(
                    "Error requesting time in lieu. Please try again in a few seconds",
                    true,
                    hideUpdateModal
                  ),
              }
            ),
      testID: "update-request",
    },
  });

  const { showModal: showDeleteModal, hideModal: hideDeleteModal } = useModal<
    ComponentProps<typeof UpdateModal>,
    { row: TimeInLieu }
  >({
    component: UpdateModal,
    data: {
      title: "Remove Time In Lieu Request",
      label: "Remove",
      description: "Are you sure you want to remove this entry?",
      onUpdate: ({ id }) =>
        deleteTimeInLieuRecord(id, {
          onSuccess: () =>
            postAction(
              "Your time in lieu request has been deleted.",
              false,
              hideDeleteModal
            ),
          onError: () =>
            postAction(
              "Error deleting time in lieu. Please try again in a few seconds",
              true,
              hideDeleteModal
            ),
        }),
    },
  });

  return {
    showCreateModal,
    showUpdateModal,
    showDeleteModal,
  };
};
